import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import RezrateVideoShell from './RezrateVideoShell';
import {
  getOrCreateRezrateVideoShellTabId,
  getRezrateVideoSession,
  parseRezrateVideoShellLocation,
  parseRezrateVideoPopupControl,
  REZRATE_VIDEO_POPUP_CONTROL_KEY,
  REZRATE_VIDEO_SHELL_HANDOFF_ACK_STORAGE_KEY,
  REZRATE_VIDEO_SHELL_HANDOFF_STORAGE_KEY,
  REZRATE_VIDEO_SHELL_PRESENCE_STORAGE_KEY,
  REZRATE_VIDEO_SHELL_PRESENCE_TTL_MS,
  setRezrateVideoActiveSessionId,
} from 'Services/rezrateVideoShell';

const SHELL_PRESENCE_HEARTBEAT_MS = 2000;

const isTopLevelShellWindow = () => {
  try {
    return window.top === window.self;
  } catch (error) {
    return true;
  }
};

const parseShellStorageRecord = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

const readShellPresenceMap = () => {
  try {
    const parsed = parseShellStorageRecord(window.localStorage.getItem(REZRATE_VIDEO_SHELL_PRESENCE_STORAGE_KEY));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeShellPresenceMap = (presenceMap) => {
  try {
    window.localStorage.setItem(REZRATE_VIDEO_SHELL_PRESENCE_STORAGE_KEY, JSON.stringify(presenceMap || {}));
  } catch (error) {
    // ignore storage errors
  }
};

const RezrateVideoShellRoute = () => {
  const location = useLocation();
  const shellTabIdRef = useRef('');
  const routeState = useMemo(() => {
    return parseRezrateVideoShellLocation(location.search);
  }, [location.search]);
  const { iframeSrc: sharedIframeSrc, sessionId, title: sharedTitle } = routeState;
  const [session, setSession] = useState(() => getRezrateVideoSession(sessionId));

  useEffect(() => {
    setSession(getRezrateVideoSession(sessionId));
  }, [sessionId]);

  const resolvedIframeSrc = session && session.iframeSrc ? session.iframeSrc : sharedIframeSrc;
  const resolvedTitle = session && session.title ? session.title : sharedTitle || 'RezRATE Meeting';

  useEffect(() => {
    if (sessionId) {
      setRezrateVideoActiveSessionId(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!isTopLevelShellWindow()) {
      return undefined;
    }

    if (!shellTabIdRef.current) {
      shellTabIdRef.current = getOrCreateRezrateVideoShellTabId();
    }

    const tabId = shellTabIdRef.current;
    const presenceMap = readShellPresenceMap();
    const recentOtherPresence = Object.values(presenceMap)
      .filter((entry) => {
        const entryTabId = `${entry && entry.tabId ? entry.tabId : ''}`.trim();
        const lastSeenAt = Number(entry && entry.lastSeenAt ? entry.lastSeenAt : 0);
        return !!entryTabId && entryTabId !== tabId && Date.now() - lastSeenAt <= REZRATE_VIDEO_SHELL_PRESENCE_TTL_MS;
      })
      .sort((left, right) => Number((right && right.lastSeenAt) || 0) - Number((left && left.lastSeenAt) || 0))[0];

    if (!recentOtherPresence || !recentOtherPresence.tabId) {
      return undefined;
    }

    const nonce = `rezrate-video-shell-handoff-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let handoffAcknowledged = false;

    const handleAckStorage = (event) => {
      if (event.key !== REZRATE_VIDEO_SHELL_HANDOFF_ACK_STORAGE_KEY) {
        return;
      }

      const ackPayload = parseShellStorageRecord(event.newValue);
      if (!ackPayload || ackPayload.nonce !== nonce || ackPayload.tabId !== recentOtherPresence.tabId) {
        return;
      }

      handoffAcknowledged = true;
      try {
        window.close();
      } catch (error) {
        // ignore close failures
      }
    };

    window.addEventListener('storage', handleAckStorage);

    try {
      window.localStorage.setItem(
        REZRATE_VIDEO_SHELL_HANDOFF_STORAGE_KEY,
        JSON.stringify({
          nonce,
          emittedAt: Date.now(),
          sessionId: `${sessionId || ''}`.trim(),
          targetHref: `${window.location.pathname || ''}${window.location.search || ''}`,
          targetTabId: recentOtherPresence.tabId,
        }),
      );
    } catch (error) {
      // ignore storage errors
    }

    const handoffTimeoutId = window.setTimeout(() => {
      window.removeEventListener('storage', handleAckStorage);
      if (!handoffAcknowledged) {
        try {
          window.localStorage.removeItem(REZRATE_VIDEO_SHELL_HANDOFF_ACK_STORAGE_KEY);
        } catch (error) {
          // ignore storage errors
        }
      }
    }, 1200);

    return () => {
      window.clearTimeout(handoffTimeoutId);
      window.removeEventListener('storage', handleAckStorage);
    };
  }, [location.pathname, location.search, sessionId]);

  useEffect(() => {
    if (!isTopLevelShellWindow()) {
      return undefined;
    }

    if (!shellTabIdRef.current) {
      shellTabIdRef.current = getOrCreateRezrateVideoShellTabId();
    }

    const tabId = shellTabIdRef.current;

    const handleStorage = (event) => {
      if (event.key === REZRATE_VIDEO_POPUP_CONTROL_KEY) {
        const payload = parseRezrateVideoPopupControl(event.newValue);
        if (!payload || payload.type !== 'REQUEST_POPUP_CLOSE') {
          return;
        }

        if (!payload.sessionId || payload.sessionId === sessionId) {
          try {
            window.close();
          } catch (error) {
            // ignore close failures
          }
        }
        return;
      }

      if (event.key === `rezrate-video-session:${sessionId}`) {
        setSession(getRezrateVideoSession(sessionId));
        return;
      }

      if (event.key === REZRATE_VIDEO_SHELL_HANDOFF_STORAGE_KEY) {
        const handoffPayload = parseShellStorageRecord(event.newValue);
        if (!handoffPayload || handoffPayload.targetTabId !== tabId) {
          return;
        }

        const targetHref = `${handoffPayload.targetHref || ''}`.trim();
        const requestedSessionId = `${handoffPayload.sessionId || ''}`.trim();
        if (requestedSessionId) {
          setRezrateVideoActiveSessionId(requestedSessionId);
          setSession(getRezrateVideoSession(requestedSessionId));
        }

        try {
          window.focus();
        } catch (error) {
          // ignore focus failures
        }

        const currentHref = `${window.location.pathname || ''}${window.location.search || ''}`;
        if (targetHref && targetHref !== currentHref) {
          window.location.replace(targetHref);
        }

        try {
          window.localStorage.setItem(
            REZRATE_VIDEO_SHELL_HANDOFF_ACK_STORAGE_KEY,
            JSON.stringify({
              nonce: `${handoffPayload.nonce || ''}`.trim(),
              tabId,
              ackedAt: Date.now(),
            }),
          );
        } catch (error) {
          // ignore storage errors
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [sessionId]);

  useEffect(() => {
    if (!isTopLevelShellWindow()) {
      return undefined;
    }

    if (!shellTabIdRef.current) {
      shellTabIdRef.current = getOrCreateRezrateVideoShellTabId();
    }

    const tabId = shellTabIdRef.current;

    const syncPresence = () => {
      const now = Date.now();
      const nextPresenceMap = {};

      Object.values(readShellPresenceMap()).forEach((entry) => {
        const entryTabId = `${entry && entry.tabId ? entry.tabId : ''}`.trim();
        const lastSeenAt = Number(entry && entry.lastSeenAt ? entry.lastSeenAt : 0);
        if (!entryTabId || entryTabId === tabId || now - lastSeenAt > REZRATE_VIDEO_SHELL_PRESENCE_TTL_MS) {
          return;
        }

        nextPresenceMap[entryTabId] = entry;
      });

      nextPresenceMap[tabId] = {
        href: `${window.location.pathname || ''}${window.location.search || ''}`,
        lastSeenAt: now,
        sessionId: `${sessionId || ''}`.trim(),
        tabId,
      };

      writeShellPresenceMap(nextPresenceMap);
    };

    const clearPresence = () => {
      const nextPresenceMap = readShellPresenceMap();
      if (nextPresenceMap[tabId]) {
        delete nextPresenceMap[tabId];
        writeShellPresenceMap(nextPresenceMap);
      }
    };

    syncPresence();
    const presenceIntervalId = window.setInterval(syncPresence, SHELL_PRESENCE_HEARTBEAT_MS);
    window.addEventListener('focus', syncPresence);

    return () => {
      window.clearInterval(presenceIntervalId);
      window.removeEventListener('focus', syncPresence);
      clearPresence();
    };
  }, [location.pathname, location.search, sessionId]);

  return (
    <div style={{ width: '100%', height: '100vh', padding: 16, backgroundColor: '#eef4fb' }}>
      <RezrateVideoShell
        emptyDescription="This meeting shell is no longer available. Return to the schedule and join again."
        emptyTitle="Meeting Unavailable"
        iframeSrc={resolvedIframeSrc}
        mode="standalone"
        title={resolvedTitle}
      />
    </div>
  );
};

export default RezrateVideoShellRoute;
